import storeManager from '../store/StoreManager';
import testQuestionsActions from '../actions/TestQuestionsActions';
import sessionReader from '../store/readHelpers/SessionReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import uiService from './UIService';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import preCheckService from './PreCheckService';
import cacheManager from '../loading/resources/cacheManager';
import tocReader from '../store/readHelpers/TocReader';
import navigationService from './NavigationService';
import parentTestService from './ParentTestService';
import stateCacheService from './StateCache';
import backgroundAudioService from './BackgroundAudioService';

const reloginService = {

    isReloginRulesContext() {
        return sessionReader.isRestoreLastVisitedQuestionEnabled() && sessionReader.isContextExam();
    },

    isResumedTestWithReloginRules() {
        return reloginService.isReloginRulesContext() && sessionReader.isResumed();
    },

    shouldStoreNextQuestion() {
        return reloginService.isReloginRulesContext() && !sessionReader.isContextReadinessCheck();
    },

    isReloginRedirectAllowed() {
        // if the test has relogin rules enabled and either it's using lockdown context or has already been resumed by a password
        return reloginService.isResumedTestWithReloginRules() && (
            !sessionReader.isTestConfiguredToRequireInvigilatorPassword() || sessionReader.isResumedTestAfterPassword()
        );
    },

    shouldStoreNextQuestionIdIfHorizontalLayoutConditions() {
        const { currentlyShown } = storeManager.getState();
        const currentSection = assessmentReader.getCurrentShownSection();
        if (currentSection && !uiService.isVerticalLayout(currentSection)) {
            if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                const lastItem = scorableItemNumberingService.getLastScorableItemInQuestion(currentlyShown.questionId);
                return currentlyShown.questionId === lastItem.contentRevisionId && currentlyShown.scorableItem === lastItem.orderNumber;
            }
            return true;
        }
        return false;
    },

    shouldStoreNextQuestionIdIfVerticalLayoutConditions(locationData = {}) {
        const currentSection = assessmentReader.getCurrentShownSection();
        if (currentSection && uiService.isVerticalLayout(currentSection)) {
            const { currentlyShown } = storeManager.getState();
            const currentSectionIndex = assessmentReader.getCurrentShownSectionIndex();
            const isFirstQuestionInSection = assessmentReader.isFirstQuestionInSection();
            const isLastQuestionInSection = assessmentReader.isLastQuestionInSection();
            const isLastSectionInAssessment = assessmentReader.isLastSectionInAssessment();
            if (isLastSectionInAssessment && isLastQuestionInSection) {
                return false;
            }
            // case when TT is navigating forward from one section to another
            if (isLastQuestionInSection) {
                if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                    // if vertical layout and if test is using useNumberPerInteraction, we want to allow storage of next question in state only if
                    // we are on the last question in the section and on the last interaction in the question and the TT navigates forward
                    const lastItem = scorableItemNumberingService.getLastScorableItemInQuestion(currentlyShown.questionId);
                    return currentlyShown.questionId === lastItem.contentRevisionId && currentlyShown.scorableItem === lastItem.orderNumber;
                }
                return true;
            }
            // case when TT is navigating back from one section to another
            if (isFirstQuestionInSection && currentSectionIndex !== 0) {
                if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                    // if vertical layout and if test is using useNumberPerInteraction, we want to allow storage of next question in state only if
                    // we are on the first question in the section and on the first interaction in the question and the TT navigates back
                    return currentlyShown.questionId !== locationData.contentRevisionId && currentlyShown.scorableItem > locationData.orderNumber;
                }
                return true;
            }
        }
        return false;
    },

    storeNextQuestionIdFlag(questionId, nextQuestionRevisionId) {
        storeManager.dispatch(
            testQuestionsActions.setNextQuestionId(questionId, nextQuestionRevisionId)
        );
    },

    // function used to call the navigation method to navigate the TT back to last visited question for a standalone test, if the test
    // has readiness check enabled and completed readiness check and if the caching is done for test resources.
    navigateToResumedQuestionIfStandaloneTestExposable() {
        const state = storeManager.getState();
        if (preCheckService.readinessCheckCompleted() && cacheManager.cacheIsDone(state.assessment.assessmentRunId)) {
            const questionIdToNavigateTo = reloginService.getQuestionIdFromResponseStateToNavigateTo();
            if (questionIdToNavigateTo) {
                const sectionId = tocReader.getSectionIdForQuestionId(questionIdToNavigateTo);
                if (sectionId) {
                    reloginService.navigateToQuestionAfterRelogin(questionIdToNavigateTo, sectionId);
                    return true;
                }
            }
        }
        return false;
    },

    navigateAfterReadinessCheckCompleted() {
        if (sessionReader.isParentTest()) {
            return parentTestService.startInProgressChildTestAfterReadinessCheckCompleted();
        }
        return reloginService.navigateToResumedQuestionIfStandaloneTestExposable();
    },

    // function will provide the path to resume the TT to latest visited question and navigate the TT to that question, if test is using
    // number per interaction, path will be build using scorable items
    navigateToQuestionAfterRelogin(questionId, sectionId) {
        let path = navigationService.getPathForQuestion(questionId, sectionId);
        const currentSection = assessmentReader.getCurrentShownSection();
        if (scorableItemNumberingService.useNumberPerInteraction() && !uiService.isVerticalLayout(currentSection)) {
            const scorableItemOrderNumber = scorableItemNumberingService.getFirstValidOrderNumberInQuestion(questionId);
            path = navigationService.getPathForScorableItem(sectionId, questionId, scorableItemOrderNumber);
        }

        return navigationService.navigateToPath(path);
    },

    // function used to navigate the TT to either latest visited question if basic layout or if last visited section if vertical layout
    navigateAfterChildTestStartAfterRelogin() {
        const questionIdToNavigateTo = reloginService.getQuestionIdFromResponseStateToNavigateTo();
        if (questionIdToNavigateTo) {
            const sectionId = tocReader.getSectionIdForQuestionId(questionIdToNavigateTo);
            if (sectionId) {
                const isBasicLayout = !uiService.isVerticalLayout(assessmentReader.getSectionById(sectionId));
                if (isBasicLayout) {
                    return navigationService.navigateToQuestion(questionIdToNavigateTo, sectionId, isBasicLayout);
                }
                return navigationService.navigateToSection(sectionId);
            }
        }
        return false;
    },

    shouldRefreshUserTestSetupUponBackOnline() {
        return sessionReader.hasEvictionBlockageReasonForTest() || sessionReader.shouldStopTimeUponExit();
    },

    getQuestionIdFromResponseStateToNavigateTo() {
        const { assessment, response } = storeManager.getState();

        const responsesArray = Object.keys(response).map(key => response[key]).filter(responseItem => responseItem.datestamp);
        const localStorageLastVisitedQuestion = stateCacheService.getStateCacheInstance().getLastVisitedQuestionObject(assessment.assessmentRunId);

        if (!localStorageLastVisitedQuestion && responsesArray.length === 0) {
            return undefined;
        }

        let latestStoredResponseObject = null;
        if (responsesArray.length !== 0) {
            latestStoredResponseObject = responsesArray.reduce(
                (acc, currentValue) => (new Date(acc.datestamp).getTime() > new Date(currentValue.datestamp).getTime() ? acc : currentValue)
            );
        }
        const latestExposedQuestion = reloginService.compareLocalStorageObjectWithResponseObject(latestStoredResponseObject,
            localStorageLastVisitedQuestion);

        if (latestExposedQuestion.nextQuestionId) {
            return latestExposedQuestion.nextQuestionId;
        }
        return latestExposedQuestion.identifier;
    },

    compareLocalStorageObjectWithResponseObject(latestStoredResponseObject, localStorageStoredObject) {
        if (!latestStoredResponseObject?.datestamp && !localStorageStoredObject?.timestampMillis) {
            return undefined;
        }
        if ((latestStoredResponseObject && localStorageStoredObject &&
                (new Date(latestStoredResponseObject.datestamp).getTime() > localStorageStoredObject.timestampMillis)) ||
            (latestStoredResponseObject && !localStorageStoredObject)) {
            return latestStoredResponseObject;
        }
        return localStorageStoredObject;
    },

    redirectedResumedTestAfterPasswordInReloginRulesContext() {
        if (reloginService.isResumedTestWithReloginRules()) {
            if (sessionReader.isParentTest()) {
                parentTestService.startInProgressChildTestIfReadinessCheckCompletedAndTestCached();
                return true;
            }
            const didAutoNavigateToLastVisitedPageInStandAloneTest = reloginService.navigateToResumedQuestionIfStandaloneTestExposable();
            if (didAutoNavigateToLastVisitedPageInStandAloneTest) {
                if (assessmentReader.hasBackgroundAudioResource()) {
                    backgroundAudioService.playAudioForNonChildTest();
                }
                return true;
            }
        }
        return false;
    },
};

export default reloginService;
